import { Link, Navigate } from "react-router-dom";
import {
  MapPin, ArrowRight, MessageSquareText, Phone, Car, Tag,
  DollarSign, Zap, ShieldCheck, HelpCircle, Truck, Users,
  Scale, Compass, MessageCircle, Star,
} from "lucide-react";
import Seo, { breadcrumbs, faqSchema, webPageSchema, serviceSchema } from "../components/Seo";
import { SectionHeader } from "../components/UI";
import CtaCallout from "../components/CtaCallout";
import LiveInventory from "../components/LiveInventory";
import { siteConfig } from "../config";
import { CITY_DATA, MODEL_LIBRARY, BUYER_INTENT_ARCHETYPES } from "../data/cityData";
import { CITY_IMAGES } from "../data/cityImages";

const ICON_FOR = {
  users: Users, zap: Zap, tag: Tag, scale: Scale, truck: Truck,
  map: MapPin, dollar: DollarSign, compass: Compass, help: HelpCircle,
  car: Car, shield: ShieldCheck,
};

const EV_HEAVY = (city) =>
  `EV interest is real around ${city}, and the Chevy lineup has options worth comparing honestly. The Equinox EV is the lowest-entry Chevy EV, the Blazer EV is midsize with stronger range options, and the Silverado EV brings real truck capability without the gas. The two factors that usually swing the decision: how many miles a day you drive, and whether you can plug in at home.`;

const EV_LIGHT = (city) =>
  `Not sure if a Chevy EV fits how you drive? Scott can help ${city} drivers compare range, charging, payment, and real-world use so you can decide whether an Equinox EV, Blazer EV, or Silverado EV makes sense — without anyone pushing you either way.`;

const TRUST_BADGES = [
  "Over 15 Years Selling Cars",
  "New & Used Vehicle Help",
  "Trade-In Guidance",
  "Payment Questions Answered",
  "EV Help Available",
  "No-Pressure Shopping",
];

const HOW_SCOTT_HELPS = [
  { Icon: Car, title: "New Chevy Help", body: "Compare new Chevy cars, trucks, SUVs, and EVs based on what you actually need, not just what shows up first in inventory." },
  { Icon: Tag, title: "Used Vehicle Help", body: "Sort used cars, trucks, and SUVs by mileage, price, payment range, condition, and long-term fit." },
  { Icon: DollarSign, title: "Trade-In Guidance", body: "Text your trade details, mileage, payoff, and condition. Scott helps you understand what matters before you start shopping." },
  { Icon: Scale, title: "Payment Questions", body: "Talk through down payment, trade value, term, taxes, fees, credit basics, lease vs finance, and realistic monthly targets." },
  { Icon: Zap, title: "EV Shopping Help", body: "Compare Equinox EV, Blazer EV, and Silverado EV with simple answers about range, charging, and real-world driving." },
  { Icon: HelpCircle, title: "Vehicle Finder Help", body: "Tell Scott what you want. If the right vehicle is not obvious, he can help watch for the right match." },
];

const HOW_IT_WORKS = [
  { n: 1, title: "Text Scott What You're Looking For", body: "Send the type of vehicle, payment range, trade details, or a link to something you like." },
  { n: 2, title: "Compare Realistic Options", body: "Scott helps you sort new, used, EV, trade, lease, finance, and payment options." },
  { n: 3, title: "Come In With a Plan", body: "Instead of walking in cold, you already know what makes sense and what questions to ask." },
];

const WHY_SCOTT_POINTS = [
  "Over 15 Years Selling Cars",
  "Personal New & Used Vehicle Help",
  "Clear Trade-In Guidance",
  "Payment Questions Explained",
  "Chevy EV Help",
  "No-Pressure Communication",
];

function StartShoppingCards({ city }) {
  const items = [
    { label: `New Chevy Vehicles Near ${city}`, to: "/new-vehicles", Icon: Car },
    { label: `Used Vehicles Near ${city}`, to: "/used-vehicles", Icon: Tag },
    { label: `New Chevy SUVs Near ${city}`, to: "/new-vehicles/suvs", Icon: Car },
    { label: `Chevy Trucks Near ${city}`, to: "/new-vehicles/trucks", Icon: Truck },
    { label: `EVs Near ${city}`, to: "/new-vehicles/evs", Icon: Zap },
    { label: `Used SUVs Near ${city}`, to: "/used-vehicles/suvs", Icon: Tag },
    { label: `Used Trucks Near ${city}`, to: "/used-vehicles/trucks", Icon: Truck },
    { label: `Vehicles Under $25,000 Near ${city}`, to: "/used-vehicles/under-25000", Icon: DollarSign },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="city-start-shopping">
      {items.map(({ label, to, Icon }) => (
        <Link
          key={label}
          to={to}
          className="ssc-card group hover:border-[var(--ssc-blue)] hover:shadow-md transition flex flex-col gap-2 min-h-[120px]"
        >
          <Icon size={18} className="text-[var(--ssc-blue)]" />
          <div className="text-sm font-extrabold text-[var(--ssc-navy)] leading-tight">{label}</div>
          <div className="mt-auto text-xs font-extrabold text-[var(--ssc-blue)] inline-flex items-center gap-1">
            Browse <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function ChevySalesCity({ slug: slugProp }) {
  const cleanSlug = (slugProp || "").replace(/^chevy-sales-/, "");
  const data = CITY_DATA[cleanSlug];
  if (!data) return <Navigate to="/" replace />;

  const { city, h1, heroSubhead, metaDesc, paragraphs, buyerIntent, models, evEmphasis, nearby, faqs, bucket } = data;
  const path = `/chevy-sales-${cleanSlug}`;
  const title = `${h1.replace(/Near .* OH$/, "").trim()} in ${city}, OH | Scott Sells Chevys`;
  // Per-slug image lookup with safe fallback to the regional default image.
  const heroImage = CITY_IMAGES[cleanSlug] || CITY_IMAGES.default;
  const heroAlt = heroImage.alt;

  const buyerCards = (buyerIntent || []).map((k) => BUYER_INTENT_ARCHETYPES[k]).filter(Boolean);
  const modelCards = (models || []).map((name) => ({ name, ...(MODEL_LIBRARY[name] || {}) })).filter((m) => m.to);
  const isEvHeavy = evEmphasis === "high";

  // Live inventory sections — chosen by the city's bucket so each page shows
  // relevant real vehicles from /api/vehicles (never static cards).
  const liveSections = [
    {
      title: `New Chevy Vehicles Near ${city}`,
      subtitle: "Live new Chevrolet inventory available from Scott right now.",
      params: { status: "new", make: "Chevrolet" },
      viewAllTo: "/new-vehicles",
      testId: `city-live-new-${cleanSlug}`,
    },
  ];
  if (bucket === "suburb-family") {
    liveSections.push({
      title: `Popular Chevy SUVs Near ${city}`,
      subtitle: "Trax, Equinox, Traverse, Tahoe, and more — live availability.",
      params: { status: "new", make: "Chevrolet", body_style: "suv" },
      viewAllTo: "/new-vehicles/suvs",
      testId: `city-live-suvs-${cleanSlug}`,
    });
  } else if (bucket === "truck-distance") {
    liveSections.push({
      title: `Chevy Trucks Near ${city}`,
      subtitle: "Silverado, Colorado, and work-ready trucks in stock now.",
      params: { make: "Chevrolet", body_style: "truck" },
      viewAllTo: "/new-vehicles/trucks",
      testId: `city-live-trucks-${cleanSlug}`,
    });
  } else {
    liveSections.push({
      title: `Used Vehicles Near ${city}`,
      subtitle: "Popular used vehicles from many brands — live availability.",
      params: { status: "used" },
      viewAllTo: "/used-vehicles",
      testId: `city-live-used-${cleanSlug}`,
    });
  }
  if (isEvHeavy) {
    liveSections.push({
      title: `EVs Available Near ${city}`,
      subtitle: "Equinox EV, Blazer EV, Silverado EV, and other electric options in stock.",
      params: { body_style: "ev" },
      viewAllTo: "/new-vehicles/evs",
      testId: `city-live-evs-${cleanSlug}`,
    });
  }

  return (
    <main id="main-content" data-testid={`chevy-sales-${cleanSlug}`}>
      <Seo
        title={title}
        description={metaDesc}
        canonicalPath={path}
        ogImage={heroImage.src}
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Central Ohio", path: "/chevy-sales-central-ohio" }, { name: `${city}, OH`, path }]),
          webPageSchema({ name: h1, path, description: metaDesc }),
          serviceSchema({
            name: `${h1.split(" Near ")[0]} for ${city}, Ohio drivers`,
            serviceType: "Personal vehicle shopping assistance",
            path,
            description: `Scott Hall helps drivers near ${city}, Ohio shop new and used Chevy vehicles, value trade-ins, talk through payment direction, and answer EV questions — no pressure, no dealership runaround.`,
          }),
          faqSchema(faqs),
        ]}
      />

      {/* ============= HERO ============= */}
      <section className="relative overflow-hidden bg-[var(--ssc-navy)] text-white">
        <img
          src={heroImage.src}
          alt={heroAlt}
          loading="eager"
          width="1600"
          height="900"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1600&q=70"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--ssc-navy)]/95 via-[var(--ssc-navy)]/80 to-[var(--ssc-navy)]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,135,255,0.18),transparent_60%)]" />
        <div className="relative ssc-container py-12 md:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--ssc-blue)]/20 border border-[var(--ssc-blue)]/40 text-[var(--ssc-blue)] text-xs font-black uppercase tracking-wide">
            <MapPin size={14} /> Local · {city}, OH
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold leading-tight max-w-4xl ssc-display" data-testid="city-h1">
            {h1}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[var(--ssc-silver)] max-w-2xl leading-relaxed">
            {heroSubhead}
          </p>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="city-hero-trust">
            {TRUST_BADGES.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-[11px] font-bold text-white">
                <Star size={11} className="text-[var(--ssc-blue)]" /> {t}
              </span>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="city-hero-ctas">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="city-text-scott">
              <MessageSquareText size={16} /> Text Scott
            </a>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-secondary">
              <HelpCircle size={16} /> Find Me a Vehicle
            </Link>
            <Link to="/new-vehicles" className="ssc-btn ssc-btn-outline">Shop Vehicles</Link>
            <Link to="/value-my-trade" className="ssc-btn ssc-btn-outline">Value My Trade</Link>
          </div>
          <p className="mt-3 text-xs text-[var(--ssc-silver)]/70">
            Tell me what you want, what you drive now, or what payment range you&apos;re trying to stay near.
          </p>
        </div>
      </section>

      {/* ============= BUYER-INTENT CARDS ============= */}
      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader title={`What ${city} Drivers Usually Compare`} subtitle="A few common starting points based on what local shoppers actually ask about." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="city-buyer-intent">
            {buyerCards.map((card, i) => {
              const Icon = ICON_FOR[card.icon] || HelpCircle;
              return (
                <Link key={i} to={card.to} className="ssc-card group hover:border-[var(--ssc-blue)] hover:shadow-md transition flex flex-col">
                  <div className="w-10 h-10 rounded-lg bg-[var(--ssc-blue)]/10 flex items-center justify-center text-[var(--ssc-blue)] mb-2">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-black text-[var(--ssc-navy)]">{card.title}</h3>
                  <p className="text-sm text-[var(--ssc-ink-2)] mt-1 leading-relaxed">{card.body(city)}</p>
                  <div className="mt-auto pt-3 text-xs font-extrabold text-[var(--ssc-blue)] inline-flex items-center gap-1">
                    Learn more <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= LOCAL INTRO ============= */}
      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--ssc-navy)]">
            Buying a Chevy Near {city} Shouldn&apos;t Feel Complicated
          </h2>
          {paragraphs.map((p, i) => (
            <p key={i} className="mt-3 text-[var(--ssc-ink)] leading-relaxed text-base md:text-lg">{p}</p>
          ))}
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary">
              <MessageSquareText size={16} /> Text Scott
            </a>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-outline">Find Me a Vehicle</Link>
          </div>
        </div>
      </section>

      {/* ============= POPULAR VEHICLES ============= */}
      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader title={`Popular Chevy Options for ${city} Shoppers`} subtitle="Based on what most local shoppers ask Scott about first." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="city-popular-models">
            {modelCards.map((m) => (
              <Link
                key={m.name}
                to={m.to}
                className="ssc-card group hover:border-[var(--ssc-blue)] hover:shadow-md transition flex flex-col"
                data-testid={`city-model-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Chevy</div>
                  {m.bestFor && (
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--ssc-navy)] bg-[var(--ssc-silver-2)] px-2 py-0.5 rounded-full">
                      Best for: {m.bestFor}
                    </span>
                  )}
                </div>
                <h3 className="font-black text-lg text-[var(--ssc-navy)] mt-2">{m.name}</h3>
                <p className="text-sm text-[var(--ssc-ink-2)] mt-1 leading-relaxed">{m.copy ? m.copy(city) : ""}</p>
                <div className="mt-auto pt-3 text-xs font-extrabold text-[var(--ssc-blue)] inline-flex items-center gap-1">
                  Ask Scott about {m.name} <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-[var(--ssc-ink-2)] mt-3 italic">
            Live availability and pricing change daily — Scott can confirm what&apos;s available right now.
          </p>
        </div>
      </section>

      {/* ============= LIVE INVENTORY ============= */}
      <section className="ssc-section bg-[var(--ssc-paper)]" data-testid={`city-live-inventory-${cleanSlug}`}>
        <div className="ssc-container space-y-10">
          {liveSections.map((s) => (
            <LiveInventory
              key={s.testId}
              title={s.title}
              subtitle={s.subtitle}
              params={s.params}
              limit={4}
              viewAllTo={s.viewAllTo}
              testId={s.testId}
            />
          ))}
        </div>
      </section>

      {/* ============= HOW SCOTT HELPS ============= */}
      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container">
          <SectionHeader title={`How Scott Helps ${city} Drivers Shop Smarter`} subtitle="Start with a text, not a sales pitch. Scott can help you narrow down vehicles, payment options, trade value, and next steps before you make the trip." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="city-how-helps">
            {HOW_SCOTT_HELPS.map(({ Icon, title: t, body }) => (
              <div key={t} className="ssc-card">
                <Icon size={20} className="text-[var(--ssc-blue)]" />
                <h3 className="font-black text-[var(--ssc-navy)] mt-2">{t}</h3>
                <p className="text-sm text-[var(--ssc-ink-2)] mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 text-center">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="city-text-wishlist">
              <MessageSquareText size={16} /> Text Scott Your Vehicle Wish List
            </a>
          </div>
        </div>
      </section>

      {/* ============= START SHOPPING ============= */}
      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader title={`Start Shopping Near ${city}`} subtitle="Already leaning a direction? Use these shortcuts, or text Scott and ask for help narrowing it down." />
          <StartShoppingCards city={city} />
          <p className="mt-4 text-sm text-[var(--ssc-ink-2)]">
            Looking for something specific near {city}? <a href={siteConfig.scott.phoneSms} className="text-[var(--ssc-blue)] font-extrabold underline">Text Scott</a> and I&apos;ll help find it.
          </p>
        </div>
      </section>

      {/* ============= TRADE + PAYMENT ============= */}
      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <h2 className="text-2xl md:text-3xl font-black text-[var(--ssc-navy)]">Trade-In and Payment Help for {city} Shoppers</h2>
              <p className="mt-3 text-[var(--ssc-ink)] leading-relaxed text-base md:text-lg">
                A lot of shoppers start with the same questions: What is my trade worth? What payment can I realistically target? Should I lease or finance? How much does my payoff matter? Scott can help you sort through those questions before you start guessing through listings.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="city-text-trade-btn">
                  <MessageSquareText size={16} /> Text Scott Your Trade Details
                </a>
                <Link to="/value-my-trade" className="ssc-btn ssc-btn-secondary">Value My Trade</Link>
                <Link to="/payment-help" className="ssc-btn ssc-btn-outline">Payment Help</Link>
              </div>
            </div>
            <aside className="ssc-card bg-white">
              <h3 className="font-black text-[var(--ssc-navy)]">Helpful info to send:</h3>
              <ul className="mt-3 space-y-2 text-sm text-[var(--ssc-ink)]">
                {["Year, make, model","Mileage","Payoff (if any)","Condition","Photos","Payment goal","Preferred vehicle type"].map((x) => (
                  <li key={x} className="flex gap-2"><ShieldCheck size={14} className="text-[var(--ssc-blue)] shrink-0 mt-1" /> {x}</li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* ============= EV SECTION ============= */}
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <div className="flex items-center gap-2 text-[var(--ssc-blue)]">
            <Zap size={18} />
            <span className="text-[10px] font-extrabold uppercase tracking-widest">EV Help</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-[var(--ssc-navy)] mt-1">
            {isEvHeavy ? `Curious About Chevy EVs Near ${city}?` : `Not Sure If a Chevy EV Fits Your Driving?`}
          </h2>
          <p className="mt-3 text-[var(--ssc-ink)] leading-relaxed text-base md:text-lg">
            {isEvHeavy ? EV_HEAVY(city) : EV_LIGHT(city)}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/ev-help" className="ssc-btn ssc-btn-primary" data-testid="city-ev-help-btn">
              <Zap size={16} /> EV Shopping Help
            </Link>
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-outline">
              <MessageSquareText size={16} /> Text Scott About EVs
            </a>
          </div>
        </div>
      </section>

      {/* ============= WHY SCOTT ============= */}
      <section className="ssc-section bg-[var(--ssc-navy)] text-white">
        <div className="ssc-container">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <SectionHeader title={`Why ${city} Drivers Text Scott First`} subtitle="Shopping alone can feel like guessing through listings, payments, trims, rebates, trade values, and fine print. Scott gives you a person to ask before you spend hours trying to decode it yourself." />
              <ul className="grid sm:grid-cols-2 gap-2 mt-4 text-sm" data-testid="city-trust-points">
                {WHY_SCOTT_POINTS.map((p) => (
                  <li key={p} className="flex items-center gap-2 font-bold text-white">
                    <ShieldCheck size={16} className="text-[var(--ssc-blue)] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <aside className="rounded-2xl bg-white/5 border border-white/15 p-5">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Side note</div>
              <h3 className="font-black text-xl mt-1">Not Sure What To Buy Yet?</h3>
              <p className="text-sm text-[var(--ssc-silver)] mt-2 leading-relaxed">
                That&apos;s normal. Text Scott what you drive now, what you like, what you hate, and what payment range you want to stay near.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary"><MessageSquareText size={14} /> Text Scott</a>
                <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-outline">Find Me a Vehicle</Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ============= HOW IT WORKS ============= */}
      <section className="ssc-section">
        <div className="ssc-container max-w-4xl">
          <SectionHeader title="How It Works" />
          <div className="grid md:grid-cols-3 gap-3" data-testid="city-how-it-works">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.n} className="ssc-card flex flex-col">
                <div className="w-9 h-9 rounded-full bg-[var(--ssc-blue)] text-white font-black text-base flex items-center justify-center">{s.n}</div>
                <h3 className="mt-3 font-black text-[var(--ssc-navy)]">{s.title}</h3>
                <p className="mt-1 text-sm text-[var(--ssc-ink-2)] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= FAQ ============= */}
      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container max-w-3xl">
          <SectionHeader title={`${city} Chevy Shopping FAQs`} />
          <div className="space-y-3" data-testid="city-faqs">
            {faqs.map((f, i) => (
              <details key={i} className="ssc-card bg-white group">
                <summary className="font-extrabold text-[var(--ssc-navy)] cursor-pointer flex items-start gap-2">
                  <MessageCircle size={16} className="text-[var(--ssc-blue)] shrink-0 mt-1" />
                  <span>{f.q}</span>
                </summary>
                <p className="mt-2 ml-6 text-[var(--ssc-ink-2)] leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============= NEARBY + INTERNAL LINKS ============= */}
      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader title={`Also Helping Drivers Near ${city}`} />
          <div className="flex flex-wrap gap-2" data-testid="city-nearby-links">
            {nearby.map((s) => {
              const c = CITY_DATA[s]?.city || s;
              return (
                <Link
                  key={s}
                  to={`/chevy-sales-${s}`}
                  className="px-4 py-2 rounded-full bg-white border border-[var(--ssc-line)] text-sm font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] hover:text-[var(--ssc-blue)] transition"
                  data-testid={`nearby-${s}`}
                >
                  <MapPin size={12} className="inline -mt-0.5 mr-1" /> {c}, OH
                </Link>
              );
            })}
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <Link to="/" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">Home</Link>
            <Link to="/chevy-sales-central-ohio" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">Central Ohio</Link>
            <Link to="/new-vehicles" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">New Vehicles</Link>
            <Link to="/used-vehicles" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">Used Vehicles</Link>
            <Link to="/ev-help" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">EV Education</Link>
            <Link to="/value-my-trade" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">Value My Trade</Link>
            <Link to="/payment-help" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">Payment Help</Link>
            <Link to="/find-me-a-vehicle" className="ssc-card text-center font-bold text-[var(--ssc-navy)] hover:border-[var(--ssc-blue)] transition">Find Me a Vehicle</Link>
          </div>
        </div>
      </section>

      {/* ============= FINAL CTA ============= */}
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <CtaCallout
            testId={`city-final-cta-${cleanSlug}`}
            variant="navy"
            heading={`Ready to Shop Near ${city} Without Guessing?`}
            sub="Text Scott what you're looking for, your trade info, or the payment range you want to stay near. He'll help you sort through real options before you spend hours shopping."
            actions={[
              { key: "text", label: "Text Scott Now", icon: "text", href: siteConfig.scott.phoneSms },
              { key: "call", label: "Call Scott", icon: "call", href: siteConfig.scott.phoneTel },
              { key: "find", label: "Find Me a Vehicle", icon: "find", to: "/find-me-a-vehicle" },
              { key: "trade", label: "Value My Trade", icon: "trade", to: "/value-my-trade" },
            ]}
          />
          <p className="text-xs text-center text-[var(--ssc-ink-2)] mt-3">
            New Chevy, used vehicles, EVs, trades, and payment help across Central Ohio.
          </p>
        </div>
      </section>
    </main>
  );
}
